// One project: photos, the facts a buyer checks first, the full description,
// amenities, location, floor plans, construction progress, price history,
// EMI and investment calculators, and the enquiry form. On a phone the
// enquiry, call and WhatsApp actions stay in a bar at the foot.
import { useEffect, useRef, useState } from 'react';
import { useSite } from '../lib/site-context.jsx';
import { formatPrice, paragraphsOf, parseYouTubeId, safeUrl, shortDate, toRupees } from '../lib/format.js';
import { ProjectCard } from '../ui/ProjectCard.jsx';
import { LeadForm } from '../ui/LeadForm.jsx';
import { Icon } from '../ui/Icon.jsx';

function Gallery({ project: p }) {
  const images = [p.hero_image, ...(p.gallery_images || [])].filter(Boolean);
  const [open, setOpen] = useState(null);
  const dlg = useRef(null);
  useEffect(() => {
    const d = dlg.current;
    if (!d) return;
    if (open !== null && !d.open) d.showModal();
    if (open === null && d.open) d.close();
  }, [open]);
  if (!images.length) {
    return (
      <div className="detail-gallery-empty" aria-hidden="true"><span>{String(p.name || '').trim().charAt(0)}</span><small>Photos coming soon</small></div>
    );
  }
  return (
    <>
      <div className={`detail-gallery${images.length === 1 ? ' detail-gallery--single' : ''}`} id="detail-gallery">
        {images.map((src, i) => (
          <button type="button" className="gallery-item" key={src + i} onClick={() => setOpen(i)} aria-label={`View photo ${i + 1} of ${images.length}`}>
            <img src={src} alt={i === 0 ? p.name : `${p.name}, photo ${i + 1}`} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" width="1200" height="800" />
          </button>
        ))}
        {images.length > 1 && <span className="gallery-count" aria-hidden="true">{images.length} photos</span>}
      </div>
      <dialog ref={dlg} className="lightbox" onClose={() => setOpen(null)} onClick={(e) => { if (e.target === dlg.current) setOpen(null); }} aria-label={`${p.name} photos`}>
        {open !== null && (
          <div className="lightbox-inner">
            <button type="button" className="icon-btn lightbox-close" aria-label="Close photo" onClick={() => setOpen(null)}><Icon name="close" size={24} /></button>
            <img src={images[open]} alt={`${p.name}, photo ${open + 1}`} />
            {images.length > 1 && (
              <div className="lightbox-nav">
                <button type="button" className="btn btn-sm" onClick={() => setOpen((open - 1 + images.length) % images.length)}>Previous</button>
                <span>{open + 1} / {images.length}</span>
                <button type="button" className="btn btn-sm" onClick={() => setOpen((open + 1) % images.length)}>Next</button>
              </div>
            )}
          </div>
        )}
      </dialog>
    </>
  );
}

function ShareLinks({ project: p }) {
  const [url, setUrl] = useState('');
  useEffect(() => { setUrl(window.location.href); }, []);
  const text = `${p.name} — ${p.tagline || 'on KeysGrove'}`;
  const u = encodeURIComponent(url);
  const links = [
    ['WhatsApp', `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${url}`)}`],
    ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${u}`],
    ['X', `https://twitter.com/intent/tweet?url=${u}&text=${encodeURIComponent(text)}`],
    ['LinkedIn', `https://www.linkedin.com/sharing/share-offsite/?url=${u}`],
    ['Telegram', `https://t.me/share/url?url=${u}&text=${encodeURIComponent(text)}`],
    ['Email', `mailto:?subject=${encodeURIComponent(text)}&body=${u}`],
  ];
  return (
    <details className="share">
      <summary><Icon name="share" size={16} /> Share</summary>
      <div className="share-buttons">
        {url && links.map(([label, href]) => <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="chip">{label}</a>)}
      </div>
    </details>
  );
}

function Facts({ project: p, inventory, settings }) {
  const secondary = settings.secondary_currency && settings.exchange_rate && (p.price_min || p.price_max)
    ? (() => {
      const conv = (v) => Math.round(toRupees(v, p.price_unit_label) / settings.exchange_rate).toLocaleString('en-US');
      return p.price_min && p.price_max ? `≈ ${settings.secondary_currency} ${conv(p.price_min)} – ${conv(p.price_max)}` : `≈ ${settings.secondary_currency} ${conv(p.price_min || p.price_max)}`;
    })()
    : '';
  const rows = [
    ['Price band', <>{formatPrice(p)}{secondary && <small className="muted"> ({secondary})</small>}</>],
    ['Status', p.status],
    ['Possession', p.possession_date || 'To be announced'],
    ['RERA number', p.rera_number || 'Applied for'],
    ['Developer', p.developer_name || '—'],
    ['Locality', p.sector || p.city || '—'],
    ...(inventory && inventory.total ? [['Availability', `${inventory.available} of ${inventory.total} units available`]] : []),
  ];
  return (
    <dl className="fact-grid" id="project-facts">
      {rows.map(([k, v]) => <div className="fact" key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
    </dl>
  );
}

function Video({ project: p }) {
  const id = parseYouTubeId(p.video_url);
  if (!p.video_url) return null;
  return (
    <section className="detail-section" id="video" aria-labelledby="h-video">
      <h2 id="h-video">Project video</h2>
      {id
        ? <div className="video-embed"><iframe src={`https://www.youtube.com/embed/${id}`} title={`${p.name} video`} loading="lazy" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
        : <p><a className="btn btn-outline btn-sm" href={safeUrl(p.video_url) || '#'} target="_blank" rel="noopener noreferrer">Watch video</a></p>}
    </section>
  );
}

function LocalityScores({ raw }) {
  let scores;
  try { scores = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
  const entries = Object.entries(scores || {}).filter(([, v]) => Number.isFinite(Number(v)));
  if (!entries.length) return null;
  return (
    <div className="locality-scores">
      {entries.map(([label, value]) => (
        <div className="locality-score-row" key={label}>
          <span className="locality-score-label">{label}</span>
          <div className="locality-score-bar" role="img" aria-label={`${label}: ${value} out of 10`}><div className="locality-score-fill" style={{ width: `${Math.max(0, Math.min(10, Number(value))) * 10}%` }} /></div>
          <span className="locality-score-value">{String(value)}/10</span>
        </div>
      ))}
    </div>
  );
}

// Standard amortisation: EMI = P·r·(1+r)^n / ((1+r)^n − 1).
function Emi({ project: p, rate, partners }) {
  const mid = p.price_min && p.price_max ? (Number(p.price_min) + Number(p.price_max)) / 2 : Number(p.price_min || p.price_max || 0);
  const [loan, setLoan] = useState(Math.round(toRupees(mid, p.price_unit_label) || 5000000));
  const [rt, setRt] = useState(rate);
  const [years, setYears] = useState(20);
  const r = (Number(rt) || 0) / 12 / 100;
  const n = (Number(years) || 1) * 12;
  const principal = Number(loan) || 0;
  const emi = r === 0 ? principal / n : (principal * r * (1 + r) ** n) / ((1 + r) ** n - 1);
  return (
    <section className="detail-section" id="emi" aria-labelledby="h-emi">
      <h2 id="h-emi">EMI calculator</h2>
      <div className="emi-calculator" id="emi-calculator">
        <div className="field"><label htmlFor="emi-loan-amount">Loan amount (₹)</label><input type="number" inputMode="numeric" id="emi-loan-amount" step="1" value={loan} onChange={(e) => setLoan(e.target.value)} /></div>
        <div className="field"><label htmlFor="emi-rate">Interest rate (% per year)</label><input type="number" inputMode="decimal" id="emi-rate" step="0.1" value={rt} onChange={(e) => setRt(e.target.value)} /></div>
        <div className="field"><label htmlFor="emi-tenure">Tenure: <span id="emi-tenure-value">{years}</span> years</label><input type="range" id="emi-tenure" min="5" max="30" value={years} onChange={(e) => setYears(e.target.value)} /></div>
        <div className="emi-result" aria-live="polite">
          <div className="emi-amount" id="emi-monthly-amount">{Number.isFinite(emi) ? `₹${Math.round(emi).toLocaleString('en-IN')}/mo` : '—'}</div>
          <div className="muted">Estimated monthly EMI</div>
        </div>
      </div>
      {partners?.length > 0 && (
        <>
          <h3>Partner banks</h3>
          <ul className="pill-row">{partners.map((m) => <li className="pill" key={m.id || m.bank_name}>{m.bank_name}{m.interest_rate ? `: ${m.interest_rate}%` : ''}</li>)}</ul>
        </>
      )}
    </section>
  );
}

function Roi({ project: p, yieldPct, appreciationPct }) {
  const mid = p.price_min && p.price_max ? (Number(p.price_min) + Number(p.price_max)) / 2 : Number(p.price_min || p.price_max || 0);
  const [price, setPrice] = useState(Math.round(toRupees(mid, p.price_unit_label) || 0) || 5000000);
  const [y, setY] = useState(p.expected_rental_yield_pct ?? yieldPct ?? 3);
  const [a, setA] = useState(p.expected_appreciation_pct ?? appreciationPct ?? 6);
  const total = ((Number(price) || 0) * ((Number(y) || 0) + (Number(a) || 0))) / 100;
  return (
    <section className="detail-section" id="roi" aria-labelledby="h-roi">
      <h2 id="h-roi">Investment estimate</h2>
      <div className="emi-calculator" id="roi-calculator">
        <div className="field"><label htmlFor="roi-price">Purchase price (₹)</label><input type="number" inputMode="numeric" id="roi-price" step="1" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
        <div className="field"><label htmlFor="roi-yield">Expected rental yield (% per year)</label><input type="number" inputMode="decimal" id="roi-yield" step="0.1" value={y} onChange={(e) => setY(e.target.value)} /></div>
        <div className="field"><label htmlFor="roi-appreciation">Expected appreciation (% per year)</label><input type="number" inputMode="decimal" id="roi-appreciation" step="0.1" value={a} onChange={(e) => setA(e.target.value)} /></div>
        <div className="emi-result" aria-live="polite">
          <div className="emi-amount" id="roi-result">₹{Math.round(total).toLocaleString('en-IN')}/yr</div>
          <div className="muted">Estimated annual rental income + appreciation</div>
        </div>
      </div>
    </section>
  );
}

export default function Project({ data }) {
  const { settings } = useSite();
  const { project: p, constructionUpdates = [], priceHistory = [], floorPlans = [], credibilityItems = [], inventory, similar = [], partners = [] } = data;
  if (!p) return <NotFound />;
  const prelaunch = p.status === 'Pre-launch';
  const testimonials = credibilityItems.filter((i) => i.type === 'testimonial');
  const sections = [
    ['overview', 'Overview'],
    ['amenities', 'Amenities'],
    ['location', 'Location'],
    ...(floorPlans.length ? [['plans', 'Floor plans']] : []),
    ...(constructionUpdates.length ? [['progress', 'Progress']] : []),
    ['emi', 'EMI'],
  ];
  const description = paragraphsOf(p.description);

  return (
    <article className="project-page" id="project-root">
      <div className="wrap">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <a href="/">Home</a><span aria-hidden="true">›</span><a href="/projects">Projects</a><span aria-hidden="true">›</span><span aria-current="page">{p.name}</span>
        </nav>
        <Gallery project={p} />

        <div className="detail-layout">
          <div className="detail-main">
            <header className="detail-title-row">
              <div>
                <span className="section-eyebrow">{p.project_type} · {p.city}</span>
                <h1>{p.name}</h1>
              </div>
              <span className="status-pill">{p.status}</span>
            </header>
            {p.tagline && <p className="tagline">{p.tagline}</p>}
            <div className="detail-actions-row"><ShareLinks project={p} /><a className="btn btn-outline btn-sm" href={`/compare?p1=${encodeURIComponent(p.id)}`}>Compare</a></div>

            <nav className="section-nav" aria-label="On this page">
              {sections.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
            </nav>

            <section className="detail-section" id="overview" aria-labelledby="h-overview">
              <h2 id="h-overview">Overview</h2>
              <Facts project={p} inventory={inventory} settings={settings} />
              <h3>Unit types</h3>
              {(p.unit_types || []).length
                ? <ul className="pill-row">{p.unit_types.map((u) => <li className="pill" key={u}>{u}</li>)}</ul>
                : <p>Details on request.</p>}
              <h3>About this project</h3>
              {description.length ? description.map((para, i) => <p key={i}>{para}</p>) : <p>Details coming soon.</p>}
              {safeUrl(p.brochure_url) && <a className="btn btn-outline" href={safeUrl(p.brochure_url)} target="_blank" rel="noopener noreferrer">Download brochure</a>}
            </section>

            <Video project={p} />
            {p.virtual_tour_url && safeUrl(p.virtual_tour_url) && (
              <section className="detail-section" id="tour" aria-labelledby="h-tour">
                <h2 id="h-tour">Virtual tour</h2>
                <div className="video-embed"><iframe src={safeUrl(p.virtual_tour_url)} title={`${p.name} virtual tour`} loading="lazy" allowFullScreen /></div>
              </section>
            )}

            <section className="detail-section" id="amenities" aria-labelledby="h-amenities">
              <h2 id="h-amenities">Amenities</h2>
              {(p.amenities || []).length
                ? <ul className="amenity-list">{p.amenities.map((a) => <li key={a}>{a}</li>)}</ul>
                : <p>Amenities list coming soon.</p>}
            </section>

            <section className="detail-section" id="location" aria-labelledby="h-location">
              <h2 id="h-location">Locality &amp; connectivity</h2>
              <p>{p.locality_notes || 'Connectivity details coming soon.'}</p>
              <LocalityScores raw={p.locality_scores} />
            </section>

            {floorPlans.length > 0 && (
              <section className="detail-section" id="plans" aria-labelledby="h-plans">
                <h2 id="h-plans">Floor plans</h2>
                <ul className="pill-row">{floorPlans.map((fp) => <li key={fp.id || fp.image_url}><a href={safeUrl(fp.image_url) || '#'} target="_blank" rel="noopener noreferrer" className="chip">{fp.label || fp.unit_type || 'View plan'}</a></li>)}</ul>
              </section>
            )}

            {constructionUpdates.length > 0 && (
              <section className="detail-section" id="progress" aria-labelledby="h-progress">
                <h2 id="h-progress">Construction progress</h2>
                <ol className="construction-timeline">
                  {constructionUpdates.map((u) => (
                    <li className="construction-update" key={u.id || u.posted_at}>
                      {u.photo_url && <img src={u.photo_url} alt={u.milestone_label || 'Construction update'} loading="lazy" decoding="async" width="240" height="160" />}
                      <div>
                        <div className="construction-update-date">{shortDate(u.posted_at)}</div>
                        <strong>{u.milestone_label}</strong>
                        <p>{u.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {priceHistory.length > 0 && (
              <section className="detail-section" id="price-history" aria-labelledby="h-price">
                <h2 id="h-price">Price history</h2>
                <dl className="fact-grid">{priceHistory.map((h) => <div className="fact" key={h.changed_at}><dt>{shortDate(h.changed_at)}</dt><dd>{formatPrice({ price_min: h.price_min, price_max: h.price_max, price_unit_label: p.price_unit_label })}</dd></div>)}</dl>
              </section>
            )}

            {settings.trust_badge_text && <div className="trust-badge">{settings.trust_badge_text}</div>}
            {testimonials.length > 0 && (
              <section className="detail-section" id="reviews" aria-labelledby="h-reviews">
                <h2 id="h-reviews">What buyers say</h2>
                <div className="quote-list">
                  {testimonials.map((q) => (
                    <figure className="quote" key={q.id || q.body}>
                      <blockquote>“{q.body}”</blockquote>
                      <figcaption><strong>{q.author_name || 'Verified buyer'}</strong>{q.rating ? <span aria-label={`${q.rating} out of 5`}> · {'★'.repeat(Math.min(5, Math.max(0, q.rating)))}</span> : null}</figcaption>
                    </figure>
                  ))}
                </div>
              </section>
            )}

            <Emi project={p} rate={settings.default_interest_rate ?? 8.5} partners={partners} />
            <Roi project={p} yieldPct={settings.default_rental_yield_pct} appreciationPct={settings.default_appreciation_pct} />
          </div>

          <aside className="enquiry-sticky" id="enquiry-card" aria-labelledby="h-enquire">
            <div className="form-panel enquiry-card">
              <h2 id="h-enquire">{prelaunch ? 'Join the waitlist' : 'Enquire about this project'}</h2>
              <p className="muted">{formatPrice(p)} · {p.status}</p>
              <LeadForm id="enquiry-form" msgId="enquiry-form-msg" prefix="ef" source={prelaunch ? 'Waitlist' : 'Website'} projectId={p.id}
                messagePlaceholder="Which unit type are you interested in?"
                submitLabel={prelaunch ? 'Join Waitlist' : 'Request a Callback'}
                successText={prelaunch ? "You're on the list — we'll notify you when bookings open." : 'Thank you — our team will call you shortly.'}
                extraLink={<a className="btn btn-outline" href={`/visit?project=${encodeURIComponent(p.id)}`}>Book Site Visit</a>} />
            </div>
          </aside>
        </div>
      </div>

      {similar.length > 0 && (
        <section className="section section-band">
          <div className="wrap">
            <span className="section-eyebrow">You might also like</span>
            <h2>Similar projects</h2>
            <div className="project-grid">{similar.map((s) => <ProjectCard key={s.id} project={s} />)}</div>
          </div>
        </section>
      )}

    </article>
  );
}

export function NotFound() {
  return (
    <section className="section">
      <div className="wrap">
        <div className="empty-state">
          <h1>Not found</h1>
          <p>We couldn&apos;t find that project. It may have been unpublished or the link is incorrect.</p>
          <a className="btn btn-outline btn-sm" href="/projects">Back to all projects</a>
        </div>
      </div>
    </section>
  );
}
